import React, { useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api, API, getToken } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import { useTeams } from '@/context/TeamContext';
import UniversalJoinPanel from '@/components/UniversalJoinPanel';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Shield, ChevronDown, Check } from 'lucide-react';
import { TID } from '@/constants/testIds';
import { Upload, Plus, Trash2, Edit2, X, Camera, Ban, RotateCcw, Archive, MessageSquare, Link as LinkIcon, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import CameraCapture from '@/components/CameraCapture';
import ShareInviteButton from '@/components/ShareInviteButton';
import InviteQRButton from '@/components/InviteQRButton';

const POSITIONS = ['PG', 'SG', 'SF', 'PF', 'C'];

function PlayerForm({ initial, onSave, onCancel, defaultCategory = 'mixed' }) {
  const { t } = useLang();
  const [data, setData] = useState({
    name: '', number: 0, position: 'SG', height_cm: 190, weight_kg: 85,
    dominant_hand: 'right', strength: '', weakness: '', ideal_role: '', photo_url: '',
    // Default to the active team's category so saving against a male/female
    // team doesn't 400 with "cannot add a 'mixed' player" — the most common
    // friction the coach hit when adding the very first player to a new team.
    category: defaultCategory,
    ...initial,
  });
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileId = r.data.id;
      const token = getToken();
      const url = `${API}/files/serve/${fileId}${token ? `?auth=${token}` : ''}`;
      setData(d => ({ ...d, photo_url: url }));
      toast.success(t('roster_photo_uploaded'));
    } catch (e) {
      toast.error(t('roster_photo_upload_error'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-card-steel border-2 border-gold/40 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-h uppercase text-xl">{initial?.id ? t('edit') : t('add_player')}</h3>
        <button onClick={onCancel} className="text-gold hover:text-alert"><X className="w-5 h-5" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-1 flex flex-col items-center gap-2">
          <div className="w-32 h-32 rounded-full border-2 border-gold bg-navy-deep overflow-hidden flex items-center justify-center">
            {data.photo_url ? (
              <img src={data.photo_url} alt="player" className="w-full h-full object-cover" />
            ) : (
              <span className="text-gold/40 text-xs">{t('roster_no_photo')}</span>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => e.target.files?.[0] && upload(e.target.files[0])}
          />
          <div className="flex gap-2 w-full" data-testid="roster-photo-actions">
            <button
              data-testid={TID.rosterUploadPhoto}
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="flex-1 text-xs px-3 py-2 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center justify-center gap-1 disabled:opacity-50"
              title={t('roster_upload_gallery_title')}
            >
              <Upload className="w-3 h-3" /> {uploading ? '...' : t('roster_upload_btn')}
            </button>
            <button
              type="button"
              data-testid="roster-camera-btn"
              disabled={uploading}
              onClick={() => setCameraOpen(true)}
              className="flex-1 text-xs px-3 py-2 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center justify-center gap-1 disabled:opacity-50"
              title={t('roster_camera_title')}
            >
              <Camera className="w-3 h-3" /> {t('roster_camera_btn')}
            </button>
          </div>
          <CameraCapture
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={(file) => upload(file)}
          />
        </div>

        <div className="sm:col-span-2 grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('name')}</label>
            <input data-testid={TID.rosterPlayerName} value={data.name} onChange={e => setData({ ...data, name: e.target.value })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('number')}</label>
            <input data-testid={TID.rosterPlayerNumber} type="number" value={data.number} onChange={e => setData({ ...data, number: Number(e.target.value) })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('position')}</label>
            <select value={data.position} onChange={e => setData({ ...data, position: e.target.value })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white">
              {POSITIONS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('dominant_hand')}</label>
            <select value={data.dominant_hand} onChange={e => setData({ ...data, dominant_hand: e.target.value })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white">
              <option value="right">{t('roster_hand_right')}</option>
              <option value="left">{t('roster_hand_left')}</option>
              <option value="ambi">{t('roster_hand_ambi')}</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('team_category')}</label>
            <select
              data-testid="roster-player-category"
              value={data.category || 'mixed'}
              onChange={e => setData({ ...data, category: e.target.value })}
              className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white"
            >
              <option value="male">{t('team_cat_male')}</option>
              <option value="female">{t('team_cat_female')}</option>
              <option value="mixed">{t('team_cat_mixed')}</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('height')} (cm)</label>
            <input type="number" value={data.height_cm} onChange={e => setData({ ...data, height_cm: Number(e.target.value) })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('weight')} (kg)</label>
            <input type="number" value={data.weight_kg} onChange={e => setData({ ...data, weight_kg: Number(e.target.value) })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('strength')}</label>
            <input value={data.strength} onChange={e => setData({ ...data, strength: e.target.value })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('weakness')}</label>
            <input value={data.weakness} onChange={e => setData({ ...data, weakness: e.target.value })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-widest text-gold font-mono">{t('ideal_role')}</label>
            <input value={data.ideal_role} onChange={e => setData({ ...data, ideal_role: e.target.value })} className="w-full bg-navy-deep border border-steel rounded px-3 py-2 outline-none text-white" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-md border border-steel text-gray-300 hover:border-gold/60">{t('cancel')}</button>
        <button data-testid={TID.rosterPlayerSave} onClick={() => onSave(data)} className="px-5 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light">{t('save')}</button>
      </div>
    </div>
  );
}

export default function Roster() {
  const { t } = useLang();
  const { user } = useAuth();
  const isPlayer = user?.role === 'player';
  const { activeTeamId, activeTeam } = useTeams();
  const [players, setPlayers] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | player
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active'); // active | blocked | archived | all

  const load = async () => {
    setLoading(true);
    try {
      // Always pass the active team explicitly. Relying on the backend's
      // session-stored active team causes a race when switchTeam() is still
      // in-flight — it would return the previous team's roster (and trigger
      // the "Aún no tiene jugadores" empty-state) until the user re-entered.
      const params = new URLSearchParams({ status: statusFilter });
      if (activeTeamId) params.set('team_id', activeTeamId);
      const r = await api.get(`/roster?${params.toString()}`);
      setPlayers(r.data.players || []);
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the active team or status filter changes
  useEffect(() => { load(); }, [activeTeamId, statusFilter]);

  const save = async (data) => {
    try {
      if (data.id) {
        await api.put(`/roster/${data.id}`, data);
        toast.success(t('roster_player_updated'));
      } else {
        await api.post('/roster', data);
        toast.success(t('roster_player_added'));
      }
      setEditing(null);
      load();
    } catch (e) {
      // Surface the backend's actual error (e.g. team↔category mismatch) so
      // the coach knows what went wrong instead of seeing the generic toast.
      const detail = e?.response?.data?.detail || t('roster_save_error');
      toast.error(detail);
    }
  };

  const del = async (id) => {
    if (!window.confirm(t('roster_delete_confirm'))) return;
    await api.delete(`/roster/${id}`);
    load();
  };

  const toggleBlock = async (p) => {
    const isBlocked = p.status === 'blocked' || !!p.blocked;
    const detail = isBlocked ? t('roster_unblock_confirm') : t('roster_block_confirm');
    if (!window.confirm(detail)) return;
    try {
      await api.patch(`/roster/${p.id}/status`, { status: isBlocked ? 'active' : 'blocked' });
      toast.success(isBlocked ? t('roster_player_unblocked') : t('roster_player_blocked'));
      load();
    } catch {
      toast.error(t('roster_action_error'));
    }
  };

  const archive = async (p) => {
    if (!window.confirm(t('roster_archive_confirm'))) return;
    try {
      await api.patch(`/roster/${p.id}/status`, { status: 'archived' });
      toast.success(t('roster_player_archived'));
      load();
    } catch {
      toast.error(t('roster_action_error'));
    }
  };

  const reactivate = async (p) => {
    try {
      await api.patch(`/roster/${p.id}/status`, { status: 'active' });
      toast.success(t('roster_player_reactivated'));
      load();
    } catch {
      toast.error(t('roster_action_error'));
    }
  };

  // F3: per-player invite — generate a link tied to the current team that the
  // player can use to register and auto-link to this team's roster.
  // Returns the share URL so the caller can hand it to the OS share-sheet.
  // We point the *shared* URL at /api/share/invite/{token} so scrapers
  // (WhatsApp, Telegram, FB) render a rich preview with the team logo.
  const invitePlayer = async (p) => {
    try {
      const { data } = await api.post('/invitations', {
        label: `Invite · ${p.name}`,
        team_id: p.team_id || activeTeamId,
      });
      const url = `${window.location.origin}/api/share/invite/${data.token}`;
      return url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      return null;
    }
  };

  const [pendingShare, setPendingShare] = useState({}); // playerId -> url

  const handLetter = (h) => h === 'right' ? t('roster_hand_letter_right') : h === 'left' ? t('roster_hand_letter_left') : t('roster_hand_letter_ambi');

  return (
    <Layout>
      <BackToDashboard />
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_roster')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('roster_h1')}</h1>
        </div>
        {!isPlayer && (
          <button
            data-testid={TID.rosterAdd}
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
          >
            <Plus className="w-4 h-4" /> {t('add_player')}
          </button>
        )}
      </div>

      {/* Universal join link panel (coaches only) — sits above the roster
          tools so it's the first thing a coach sees when adding players. */}
      {!isPlayer && <UniversalJoinPanel />}

      {!isPlayer && editing && (
        <div className="mb-6">
          <PlayerForm
            initial={editing}
            onSave={save}
            onCancel={() => setEditing(null)}
            defaultCategory={activeTeam?.category || 'mixed'}
          />
        </div>
      )}

      {/* Status filter pills + team switcher — coaches only */}
      {!isPlayer && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-gold mr-2">{t('roster_status_filter')}:</span>
          {['active', 'blocked', 'archived', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`roster-filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-h uppercase tracking-wider border ${
                statusFilter === s ? 'bg-gold text-navy border-gold' : 'border-gold/30 text-gold hover:bg-gold/10'
              }`}
            >
              {t(`roster_status_${s}`)}
            </button>
          ))}
          <RosterTeamSwitcher />
        </div>
      )}

      {loading && <div className="text-gray-400">{t('loading')}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {players.map(p => {
          const status = p.status || (p.blocked ? 'blocked' : 'active');
          const isBlocked = status === 'blocked';
          const isArchived = status === 'archived';
          const dimmed = isBlocked || isArchived;
          const borderCls = isBlocked ? 'border-alert/60' : isArchived ? 'border-steel' : 'border-gold/40';
          return (
          <div key={p.id} data-testid={`${TID.rosterPlayerCard}-${p.id}`} className={`bg-card-steel border-2 ${borderCls} ${dimmed ? 'opacity-70' : ''} rounded-lg overflow-hidden hover-lift relative`}>
            {(isBlocked || isArchived) && (
              <div className={`absolute top-2 left-2 z-10 ${isBlocked ? 'bg-alert' : 'bg-steel'} text-white text-[10px] font-h uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1`} data-testid={`roster-status-badge-${p.id}`}>
                <Ban className="w-3 h-3" /> {t(`roster_status_${status}`)}
              </div>
            )}
            <div className="aspect-[4/3] bg-navy-deep border-b border-gold/30 relative overflow-hidden">
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name} className={`w-full h-full object-cover ${dimmed ? 'grayscale' : ''}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gold/40 font-h text-5xl">#{p.number}</div>
              )}
              <div className="absolute top-2 right-2 bg-navy-deep/90 border border-gold/60 rounded-md px-2 py-0.5 text-xs font-mono text-gold">
                #{p.number} · {p.position}
              </div>
              {p.category && p.category !== 'mixed' && (
                <div className="absolute bottom-2 right-2 bg-navy-deep/90 border border-gold/40 rounded-md px-2 py-0.5 text-[10px] font-h uppercase tracking-wider text-gold">
                  {t(`team_cat_${p.category}`)}
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="font-h uppercase text-lg text-white">{p.name}</div>
              <div className="text-xs text-gray-400 mt-1">{p.height_cm}cm · {p.weight_kg}kg · {handLetter(p.dominant_hand)}</div>
              {p.strength && <div className="mt-2 text-xs text-gray-300 line-clamp-2"><span className="text-gold uppercase tracking-wider">{t('strength')}:</span> {p.strength}</div>}
              <div className="mt-3 flex gap-2 flex-wrap">
                {isPlayer ? null : isArchived ? (
                  <button onClick={() => reactivate(p)} data-testid={`roster-reactivate-${p.id}`} className="flex-1 text-xs px-3 py-1.5 rounded border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10 inline-flex items-center justify-center gap-1">
                    <RotateCcw className="w-3 h-3" /> {t('roster_reactivate')}
                  </button>
                ) : (
                  <>
                    <button onClick={() => setEditing(p)} className="flex-1 min-w-[80px] text-xs px-3 py-1.5 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center justify-center gap-1">
                      <Edit2 className="w-3 h-3" /> {t('edit')}
                    </button>
                    {p.user_id ? (
                      <Link
                        to={`/notes?to=${p.user_id}`}
                        data-testid={`roster-note-${p.id}`}
                        title={t('roster_send_note')}
                        className="px-3 py-1.5 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center justify-center"
                      >
                        <MessageSquare className="w-3 h-3" />
                      </Link>
                    ) : pendingShare[p.id] ? (
                      <>
                        <ShareInviteButton
                          url={pendingShare[p.id]}
                          title={t('invite_share_title', { name: p.name })}
                          text={t('invite_share_text', { name: p.name, team: activeTeam?.name || '', url: pendingShare[p.id] })}
                          variant="compact"
                          data-testid={`roster-share-${p.id}`}
                        />
                        <InviteQRButton
                          url={pendingShare[p.id]}
                          label={`${p.name} · ${activeTeam?.name || ''}`.trim()}
                          logoUrl={activeTeam?.logo_url || null}
                          variant="compact"
                          data-testid={`roster-qr-${p.id}`}
                        />
                      </>
                    ) : (
                      <button
                        onClick={async () => {
                          const url = await invitePlayer(p);
                          if (url) {
                            setPendingShare(s => ({ ...s, [p.id]: url }));
                            // If the device supports native sharing, fire it immediately.
                            if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
                              try {
                                await navigator.share({
                                  title: t('invite_share_title', { name: p.name }),
                                  text: t('invite_share_native_text', { name: p.name, team: activeTeam?.name || '' }),
                                  url,
                                });
                              } catch {/* user cancelled, button stays for retry */}
                            } else {
                              toast.success(t('roster_invite_ready'));
                            }
                          }
                        }}
                        data-testid={`roster-invite-${p.id}`}
                        title={t('roster_invite_title')}
                        className="px-3 py-1.5 rounded border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10 inline-flex items-center justify-center gap-1 text-xs"
                      >
                        <UserPlus className="w-3 h-3" /> {t('roster_invite_btn')}
                      </button>
                    )}
                    <button
                      onClick={() => toggleBlock(p)}
                      data-testid={`roster-block-toggle-${p.id}`}
                      title={isBlocked ? t('roster_unblock_title') : t('roster_block_title')}
                      className={`px-3 py-1.5 rounded border inline-flex items-center justify-center gap-1 text-xs ${isBlocked ? 'border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10' : 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10'}`}
                    >
                      {isBlocked ? <RotateCcw className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                      {isBlocked ? t('roster_unblock_btn') : t('roster_block_btn')}
                    </button>
                    <button onClick={() => archive(p)} data-testid={`roster-archive-${p.id}`} title={t('roster_archive_title')} className="px-3 py-1.5 rounded border border-steel text-gray-300 hover:bg-steel/30">
                      <Archive className="w-3 h-3" />
                    </button>
                  </>
                )}
                {!isPlayer && (
                  <button
                    onClick={() => del(p.id)}
                    title={t('roster_delete_title')}
                    className="px-3 py-1.5 rounded border border-alert/50 text-alert hover:bg-alert/10"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {!loading && !players.length && (
        <div className="text-center text-gray-400 py-12 border-2 border-dashed border-gold/30 rounded-lg">
          {t('roster_no_players')} <span className="text-gold">{t('add_player')}</span>.
        </div>
      )}
    </Layout>
  );
}

function RosterTeamSwitcher() {
  const { t } = useLang();
  const { teams, activeTeam, switchTeam } = useTeams();
  const [open, setOpen] = React.useState(false);
  if (!teams || teams.length <= 1) {
    return activeTeam ? (
      <span className="ml-auto text-xs text-gray-400">
        <span className="text-gold">{t('team_active')}:</span> {activeTeam.name}
      </span>
    ) : null;
  }
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        data-testid="roster-team-switcher"
        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-h uppercase tracking-wider border-2 border-gold text-gold bg-gold/10 hover:bg-gold/20"
      >
        <Shield className="w-3 h-3" /> {t('team_choose')}:
        <span className="font-mono normal-case ml-1 text-white">{activeTeam?.name || '—'}</span>
        <ChevronDown className="w-3 h-3 ml-1" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-navy-deep border-gold/30">
        {teams.map(t2 => (
          <DropdownMenuItem
            key={t2.id}
            data-testid={`roster-team-pick-${t2.id}`}
            onClick={() => { switchTeam(t2.id); setOpen(false); }}
            className="text-sm hover:bg-gold/10 cursor-pointer flex items-center justify-between gap-3"
          >
            <span>{t2.name}</span>
            {activeTeam?.id === t2.id && <Check className="w-3.5 h-3.5 text-gold" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
